/* eslint no-console: 0 */
import * as types from '../mutation-types';
import Report from '../../api/reports';
import { downloadCsvFile, downloadXlsxFile, generateFileName } from '../../helper/downloadHelper';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { REPORTS_EVENTS } from '../../helper/AnalyticsHelper/events';
import {
  reconcileHeatmapData,
  clampDataBetweenTimeline,
} from 'shared/helpers/ReportsDataHelper';

const state = {
  fetchingStatus: false,
  accountReport: {
    isFetching: {
      conversations_count: false,
      incoming_messages_count: false,
      outgoing_messages_count: false,
      avg_first_response_time: false,
      avg_resolution_time: false,
      resolutions_count: false,
      bot_resolutions_count: false,
      bot_handoffs_count: false,
      reply_time: false,
    },
    data: {
      conversations_count: [],
      incoming_messages_count: [],
      outgoing_messages_count: [],
      avg_first_response_time: [],
      avg_resolution_time: [],
      resolutions_count: [],
      bot_resolutions_count: [],
      bot_handoffs_count: [],
      reply_time: []
    },
  },
  accountSummary: {
    avg_first_response_time: 0,
    avg_resolution_time: 0,
    conversations_count: 0,
    incoming_messages_count: 0,
    outgoing_messages_count: 0,
    reply_time: 0,
    resolutions_count: 0,
    bot_resolutions_count: 0,
    bot_handoffs_count: 0,
    previous: {},
  },
  botSummary: {
    bot_resolutions_count: 0,
    bot_handoffs_count: 0,
    previous: {},
  },
  overview: {
    uiFlags: {
      isFetchingAccountConversationMetric: false,
      isFetchingAccountConversationsHeatmap: false,
      isFetchingAgentConversationMetric: false,
      isFetchingTeamMetrics: false,
      isFetchingInboxMetrics: false,
      isFetchingLabelMetrics: false
    },
    accountConversationMetric: {},
    accountConversationHeatmap: [],
    agentConversationMetric: [],
    teamMetrics: [],
    inboxMetrics: [],
    labelMetrics: []
  },
};

const getters = {
  getAccountReports(_state) {
    return _state.accountReport;
  },
  getAccountSummary(_state) {
    return _state.accountSummary;
  },
  getBotSummary(_state) {
    return _state.botSummary;
  },
  getAccountConversationMetric(_state) {
    return _state.overview.accountConversationMetric;
  },
  getAccountConversationHeatmapData(_state) {
    return _state.overview.accountConversationHeatmap;
  },
  getAgentConversationMetric(_state) {
    return _state.overview.agentConversationMetric;
  },
  getOverviewUIFlags($state) {
    return $state.overview.uiFlags;
  },
  getTeamMetrics: _$state => _$state.overview.teamMetrics,
  getInboxMetrics: _$state => _$state.overview.inboxMetrics,
  getLabelMetrics: (_state, _getters, _rootState, rootGetters) => rootGetters['labels/getLabels'],
};

export const actions = {
  fetchAccountReport({ commit }, reportObj) {
    const { metric } = reportObj;
    commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
      metric,
      value: true,
    });
    Report.getReports(reportObj).then(accountReport => {
      let { data } = accountReport;
      data = clampDataBetweenTimeline(data, reportObj.from, reportObj.to);
      commit(types.default.SET_ACCOUNT_REPORTS, {
        metric,
        data,
      });
      commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
        metric,
        value: false,
      });
    });
  },
  fetchAccountConversationHeatmap({ commit }, reportObj) {
    commit(types.default.TOGGLE_HEATMAP_LOADING, true);
    Report.getReports({ ...reportObj, groupBy: 'hour' }).then(heatmapData => {
      let { data } = heatmapData;
      data = clampDataBetweenTimeline(data, reportObj.from, reportObj.to);

      data = reconcileHeatmapData(
        data,
        state.overview.accountConversationHeatmap
      );

      commit(types.default.SET_HEATMAP_DATA, data);
      commit(types.default.TOGGLE_HEATMAP_LOADING, false);
    });
  },
  fetchAccountSummary({ commit }, reportObj) {
    Report.getSummary(
      reportObj.from,
      reportObj.to,
      reportObj.type,
      reportObj.id,
      reportObj.groupBy,
      reportObj.businessHours
    )
      .then(accountSummary => {
        commit(types.default.SET_ACCOUNT_SUMMARY, accountSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, false);
      });
  },
  fetchBotSummary({ commit }, reportObj) {
    Report.getBotSummary({
      from: reportObj.from,
      to: reportObj.to,
      groupBy: reportObj.groupBy,
      businessHours: reportObj.businessHours,
    })
      .then(botSummary => {
        commit(types.default.SET_BOT_SUMMARY, botSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, false);
      });
  },
  fetchAccountConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, true);
    Report.getConversationMetric(reportObj.type)
      .then(accountConversationMetric => {
        commit(
          types.default.SET_ACCOUNT_CONVERSATION_METRIC,
          accountConversationMetric.data
        );
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      });
  },
  async fetchAgentConversationMetric({ commit }, { type, page = 1, append = false }) {
    commit(types.default.SET_AGENT_CONVERSATION_METRIC_UI_FLAGS, {
      isFetchingAgentConversationMetric: true
    });
  
    try {
      const response = await Report.getConversationMetric(type, page);
      const agentMetrics = response.data || [];
      
      if (append) {
        commit(types.default.APPEND_AGENT_CONVERSATION_METRIC, agentMetrics);
      } else {
        commit(types.default.SET_AGENT_CONVERSATION_METRIC, agentMetrics);
      }
  
      return {
        data: agentMetrics,
        hasMore: agentMetrics.length === 9 // 9 items per page
      };
    } finally {
      commit(types.default.SET_AGENT_CONVERSATION_METRIC_UI_FLAGS, {
        isFetchingAgentConversationMetric: false
      });
    }
  },
  downloadAgentReports(_, reportObj) {
    return Report.getAgentReportsXlsx(reportObj)
      .then(response => {
        const fileName = (reportObj.fileName || '').replace(/\.csv$/i, '.xlsx');
        downloadXlsxFile(fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'agent',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadLabelReports(_, reportObj) {
    return Report.getLabelReportsXlsx(reportObj)
      .then(response => {
        const fileName = (reportObj.fileName || '').replace(/\.csv$/i, '.xlsx');
        downloadXlsxFile(fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'label',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadInboxReports(_, reportObj) {
    return Report.getInboxReportsXlsx(reportObj)
      .then(response => {
        const fileName = (reportObj.fileName || '').replace(/\.csv$/i, '.xlsx');
        downloadXlsxFile(fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'inbox',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadTeamReports(_, reportObj) {
    return Report.getTeamReportsXlsx(reportObj)
      .then(response => {
        const fileName = (reportObj.fileName || '').replace(/\.csv$/i, '.xlsx');
        downloadXlsxFile(fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'team',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadBotReports(_, reportObj) {
    return Report.getBotReportsXlsx(reportObj)
      .then(response => {
        const fileName = (reportObj.fileName || '').replace(/\.csv$/i, '.xlsx');
        downloadXlsxFile(fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'bot',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadAccountConversationHeatmap(_, reportObj) {
    Report.getConversationTrafficCSV()
      .then(response => {
        downloadCsvFile(
          generateFileName({
            type: 'Conversation traffic',
            to: reportObj.to,
          }),
          response.data
        );

        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'conversation_heatmap',
          businessHours: false,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
    fetchTeamMetrics({ commit }, reportObj) {
    commit(types.default.TOGGLE_TEAM_METRICS_LOADING, true);
    try {
      // Use real data from v2 reports teams endpoint
      Report.getTeamReports({
        from: reportObj?.from,
        to: reportObj?.to,
        businessHours: reportObj?.businessHours,
      }).then(({ data }) => {
        commit('SET_TEAM_METRICS', data);
      }).catch(error => {
        console.error('Error fetching team metrics:', error);
        commit('SET_TEAM_METRICS', []);
      });
    } catch (error) {
      // ignore synchronous errors; async errors handled above
    } finally {
      commit(types.default.TOGGLE_TEAM_METRICS_LOADING, false);
    }
  },

    fetchInboxMetrics({ commit }, reportObj) {
    commit(types.default.TOGGLE_INBOX_METRICS_LOADING, true);
    try {
      Report.getInboxReports({
        from: reportObj?.from,
        to: reportObj?.to,
        businessHours: reportObj?.businessHours,
      }).then(({ data }) => {
        commit('SET_INBOX_METRICS', data);
      }).catch(error => {
        console.error('Error fetching inbox metrics:', error);
        commit('SET_INBOX_METRICS', []);
      });
    } catch (error) {
      // ignore synchronous errors; async errors handled above
    } finally {
      commit(types.default.TOGGLE_INBOX_METRICS_LOADING, false);
    }
  },
  fetchLabelMetrics({ commit, rootGetters }) {
    commit(types.default.TOGGLE_LABEL_METRICS_LOADING, true);
    try {
      const data = rootGetters['labels/getLabels'] || [];
      commit(types.default.SET_LABEL_METRICS, data);
    } finally {
      commit(types.default.TOGGLE_LABEL_METRICS_LOADING, false);
    }
  },

};

const mutations = {
  [types.default.SET_ACCOUNT_REPORTS](_state, { metric, data }) {
    _state.accountReport.data[metric] = data;
  },
  [types.default.SET_HEATMAP_DATA](_state, heatmapData) {
    _state.overview.accountConversationHeatmap = heatmapData;
  },
  [types.default.TOGGLE_ACCOUNT_REPORT_LOADING](_state, { metric, value }) {
    _state.accountReport.isFetching[metric] = value;
  },
  [types.default.TOGGLE_HEATMAP_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationsHeatmap = flag;
  },
  [types.default.SET_ACCOUNT_SUMMARY](_state, summaryData) {
    _state.accountSummary = summaryData;
  },
  [types.default.SET_BOT_SUMMARY](_state, summaryData) {
    _state.botSummary = summaryData;
  },
  [types.default.SET_ACCOUNT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.accountConversationMetric = metricData;
  },
  [types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationMetric = flag;
  },
  [types.default.SET_AGENT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.agentConversationMetric = metricData;
  },
  [types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAgentConversationMetric = flag;
  },
  [types.default.SET_TEAM_METRICS](_state, data) {
    _state.overview.teamMetrics = data;
  },
  [types.default.SET_INBOX_METRICS](_state, data) {
    _state.overview.inboxMetrics = data;
  },
  [types.default.TOGGLE_TEAM_METRICS_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingTeamMetrics = flag;
  },
  [types.default.TOGGLE_INBOX_METRICS_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingInboxMetrics = flag;
  },
  [types.default.SET_LABEL_METRICS](_state, data) {
    _state.overview.labelMetrics = data;
  },
  [types.default.TOGGLE_LABEL_METRICS_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingLabelMetrics = flag;
  },
  [types.default.APPEND_AGENT_CONVERSATION_METRIC](_state, data) {
    _state.overview.agentConversationMetric = [
      ..._state.overview.agentConversationMetric,
      ...data
    ];
  },
  [types.default.SET_AGENT_CONVERSATION_METRIC_UI_FLAGS](_state, data) {
    _state.overview.uiFlags = {
      ..._state.overview.uiFlags,
      ...data
    };
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
